import { ArrowDownOutlined, ArrowUpOutlined, AuditOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Flex, Segmented, Skeleton, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import ComplianceDashboardHeader from "../../components/Compliance/ComplianceDashboardHeader.component";
import ComplianceEmptyState from "../../components/Compliance/ComplianceEmptyState.component";
import ComplianceTabs from "../../components/Compliance/ComplianceTabs.component";
import { TONE } from "../../components/Compliance/findings.shared";
import { useAppSelector } from "../../hooks/redux-hooks";
import { type RuleCaptureRow, useGetComplianceDashboardQuery } from "../../service/compliance.service";
import styles from "./compliance.module.scss";

const statusMeta: Record<string, { color: string; bg: string; label: string }> = {
  needs_attention: { color: TONE.danger.fg, bg: TONE.danger.bg, label: "Needs attention" },
  watch: { color: TONE.warning.fg, bg: TONE.warning.bg, label: "Watch" },
  on_track: { color: TONE.success.fg, bg: TONE.success.bg, label: "On track" },
};

const DAYS_OPTIONS = [
  { label: "7d", value: 7 },
  { label: "30d", value: 30 },
  { label: "90d", value: 90 },
];

// ─── Rule capture analytics ──────────────────────────────────────────────────

const BreakdownBar = ({ row }: { row: RuleCaptureRow }) => (
  <Flex vertical gap={4} style={{ minWidth: 160 }}>
    <Flex style={{ height: 10, borderRadius: 5, overflow: "hidden", background: "#f4f5f8" }}>
      <div style={{ width: `${row.pass_pct}%`, background: TONE.success.accent }} />
      <div style={{ width: `${row.partial_pct}%`, background: TONE.warning.accent }} />
      <div style={{ width: `${row.fail_pct}%`, background: TONE.danger.border }} />
    </Flex>
    <Flex gap={8} style={{ fontSize: 10.5, color: "#6b7280" }}>
      <span>Pass {row.pass_pct}%</span>
      <span>Partial {row.partial_pct}%</span>
      <span>Fail {row.fail_pct}%</span>
    </Flex>
  </Flex>
);

const ruleColumns = (onViewFindings: (rule: RuleCaptureRow) => void): ColumnsType<RuleCaptureRow> => [
  {
    title: "Rule",
    dataIndex: "rule_name",
    key: "rule",
    render: (_value, row) => (
      <Flex vertical>
        <Typography.Text strong>{row.rule_name}</Typography.Text>
        {row.custom_note_name ? (
          <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>from "{row.custom_note_name}"</Typography.Text>
        ) : null}
      </Flex>
    ),
  },
  {
    title: "Capture",
    dataIndex: "capture_rate",
    key: "capture",
    width: 130,
    render: (value: number, row) => {
      const tier = value < 33 ? "low" : value < 67 ? "mid" : "good";
      const color = tier === "low" ? TONE.danger.fg : tier === "mid" ? TONE.warning.fg : TONE.success.fg;
      return (
        <Typography.Text strong style={{ fontSize: 16, color }}>
          {value}%
          <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 4, fontWeight: 400 }}>n={row.sample_size}</span>
        </Typography.Text>
      );
    },
  },
  {
    title: "Breakdown",
    key: "breakdown",
    render: (_value, row) => <BreakdownBar row={row} />,
  },
  {
    title: "Trend",
    dataIndex: "delta_pts",
    key: "trend",
    width: 100,
    render: (delta: number | null) => {
      if (delta === null) return <Typography.Text style={{ color: "#9ca3af", fontSize: 12 }}>—</Typography.Text>;
      if (delta === 0) return <Typography.Text style={{ color: "#6b7280", fontSize: 12 }}>flat</Typography.Text>;
      const up = delta > 0;
      return (
        <Flex align="center" gap={4}>
          {up ? (
            <ArrowUpOutlined style={{ color: TONE.success.accent, fontSize: 11 }} />
          ) : (
            <ArrowDownOutlined style={{ color: TONE.danger.accent, fontSize: 11 }} />
          )}
          <Typography.Text
            style={{ color: up ? TONE.success.accent : TONE.danger.accent, fontSize: 12, fontWeight: 500 }}
          >
            {Math.abs(delta)} pts
          </Typography.Text>
        </Flex>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 140,
    render: (value: keyof typeof statusMeta) => {
      const meta = statusMeta[value];
      return (
        <span style={{ background: meta.bg, color: meta.color, padding: "3px 9px", borderRadius: 999, fontSize: 12 }}>
          {meta.label}
        </span>
      );
    },
  },
  {
    title: "",
    key: "actions",
    width: 90,
    render: (_value, row) => (
      <Button
        type="link"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onViewFindings(row);
        }}
      >
        Findings →
      </Button>
    ),
  },
];

// ─── Rules-by-template listing ───────────────────────────────────────────────

// ─── Page ────────────────────────────────────────────────────────────────────

const RulesProgram = () => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const [days, setDays] = useState<number>(30);
  const { data: dashData, isLoading: dashLoading, refetch, isFetching } = useGetComplianceDashboardQuery({ days });
  const [searchParams] = useSearchParams();
  const hasRules = searchParams.get("empty") !== "1";

  if (!userInfo?.is_compliance_enabled || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }

  if (!hasRules) {
    return (
      <div className={styles.compliance__container}>
        <ComplianceDashboardHeader />
        <ComplianceTabs />
        <ComplianceEmptyState
          icon={<AuditOutlined />}
          title="No rule analytics yet"
          description="Rule capture analytics show how each rule performs across notes generated in the window. Add rules per template to start seeing pass / partial / fail breakdowns and trends here."
        />
      </div>
    );
  }

  return (
    <div className={styles.compliance__container}>
      <ComplianceDashboardHeader />

      <ComplianceTabs />

      <Flex justify="space-between" align="center" gap={12} wrap="wrap" style={{ marginBottom: 16 }}>
        <Flex vertical>
          <Typography.Text strong style={{ fontSize: 16 }}>
            Rule capture
          </Typography.Text>
          <Typography.Text style={{ color: "#6b7280", fontSize: 12, fontWeight: 400 }}>
            How each rule performs across notes generated in the window. Click "Findings →" to triage open cases.
          </Typography.Text>
        </Flex>
        <Flex align="center" gap={8}>
          <Segmented options={DAYS_OPTIONS} value={days} onChange={(v) => setDays(Number(v))} disabled={isFetching} />
          <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
        </Flex>
      </Flex>

      {dashLoading ? (
        <Skeleton active title={{ width: "30%" }} paragraph={{ rows: 8 }} style={{ padding: "8px 0" }} />
      ) : (
        <Table<RuleCaptureRow>
          columns={ruleColumns(() => navigate("/compliance"))}
          dataSource={dashData?.rule_capture ?? []}
          rowKey={(row) => `${row.custom_note_id ?? "none"}::${row.rule_id}`}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: "No findings recorded yet for this window." }}
          size="middle"
        />
      )}
    </div>
  );
};

export default RulesProgram;
